import { z } from 'zod';
import { AstorAutonomyLevel, DEFAULT_ASTOR_AUTONOMY_LEVEL, SubscriptionStatus, SubscriptionTier } from '../enums';
import { SignupQualifiersSchema } from '../schemas/metadata.schemas';
import { nameSchema, slugSchema } from '../validation/slugs';

/**
 * Business DTO Schema
 *
 * A Business is the third level of the three-tier isolation hierarchy:
 * Tenant (Infrastructure) > Realm (Database) > Business (Data)
 *
 * Businesses provide row-level data isolation within a Realm.
 * Billing can be at Tenant, Realm, or Business level (flexible).
 */
export const BusinessSchema = z.object({
  id: z.string().uuid(),
  realmId: z.string().uuid(),

  // Identification
  name: nameSchema,
  slug: slugSchema,
  domain: z.string().nullable().optional(),
  description: z.string().nullable().optional(),

  // Billing (optional at this level - can inherit from Realm/Tenant)
  stripeCustomerId: z.string().nullable().optional(),
  subscriptionTier: z.nativeEnum(SubscriptionTier).nullable().optional(),
  subscriptionStatus: z.nativeEnum(SubscriptionStatus).nullable().optional(),

  // Qualification/Onboarding
  signupQualifiers: SignupQualifiersSchema.nullable().optional(),
  industry: z.string().nullable().optional(),
  companySize: z.enum(['small', 'medium', 'large', 'enterprise']).nullable().optional(),

  // Status
  isActive: z.boolean().default(true),
  isSandbox: z.boolean().default(false),

  // Astor autonomy — platform-wide master level (UX v3 #1739).
  // Per-agent overrides live on `BicAgent.autonomyLevel` (8-agent-service).
  autonomyLevel: z.nativeEnum(AstorAutonomyLevel).default(DEFAULT_ASTOR_AUTONOMY_LEVEL),

  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Business = z.infer<typeof BusinessSchema>;

/**
 * Create Business DTO Schema
 */
export const CreateBusinessSchema = z.object({
  realmId: z.string().uuid(),
  name: nameSchema,
  slug: slugSchema.optional(), // Auto-generated if not provided
  domain: z.string().nullable().optional(),
  description: z.string().nullable().optional(),

  // Optional billing (inherit from Realm/Tenant if not provided)
  stripeCustomerId: z.string().nullable().optional(),
  subscriptionTier: z.nativeEnum(SubscriptionTier).nullable().optional(),

  // Qualification
  signupQualifiers: SignupQualifiersSchema.nullable().optional(),
  industry: z.string().nullable().optional(),
  companySize: z.enum(['small', 'medium', 'large', 'enterprise']).nullable().optional(),

  isSandbox: z.boolean().default(false),

  // Admin portal: optional business owner invitation on creation
  adminEmail: z.string().email().optional(),
  adminName: z.string().min(1).max(255).optional(),
});

export type CreateBusiness = z.infer<typeof CreateBusinessSchema>;

/**
 * Update Business DTO Schema
 */
export const UpdateBusinessSchema = z
  .object({
    name: nameSchema.optional(),
    slug: slugSchema.optional(),
    domain: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    stripeCustomerId: z.string().nullable().optional(),
    subscriptionTier: z.nativeEnum(SubscriptionTier).nullable().optional(),
    subscriptionStatus: z.nativeEnum(SubscriptionStatus).nullable().optional(),
    signupQualifiers: SignupQualifiersSchema.nullable().optional(),
    industry: z.string().nullable().optional(),
    companySize: z.enum(['small', 'medium', 'large', 'enterprise']).nullable().optional(),
    isActive: z.boolean().optional(),
    autonomyLevel: z.nativeEnum(AstorAutonomyLevel).optional(),
  })
  .refine((data) => Object.keys(data).length > 0, {
    message: 'At least one field must be provided for update',
  });

export type UpdateBusiness = z.infer<typeof UpdateBusinessSchema>;

/**
 * Standalone schema for the dedicated `business.updateAutonomyLevel` mutation
 * — small, single-purpose, lives in shared-types so the frontend and gateway
 * agree on shape.
 */
export const UpdateBusinessAutonomyLevelSchema = z.object({
  autonomyLevel: z.nativeEnum(AstorAutonomyLevel),
});

export type UpdateBusinessAutonomyLevel = z.infer<typeof UpdateBusinessAutonomyLevelSchema>;

/**
 * Business with effective billing (resolved from Tenant/Realm/Business)
 */
export const BusinessWithBillingSchema = BusinessSchema.extend({
  effectiveBilling: z.object({
    level: z.enum(['tenant', 'realm', 'business']),
    stripeCustomerId: z.string().nullable(),
    subscriptionTier: z.nativeEnum(SubscriptionTier),
    subscriptionStatus: z.nativeEnum(SubscriptionStatus),
  }),
});

export type BusinessWithBilling = z.infer<typeof BusinessWithBillingSchema>;

/**
 * Business user (employee of a business)
 */
export const BusinessUserSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  email: z.string().email(),
  name: z.string().min(1).max(255),
  role: z.enum(['admin', 'member', 'viewer']),
  isActive: z.boolean().default(true),
  createdAt: z.string().datetime(),
});

export type BusinessUser = z.infer<typeof BusinessUserSchema>;

/**
 * Create Business User DTO
 */
export const CreateBusinessUserSchema = z.object({
  businessId: z.string().uuid(),
  email: z.string().email(),
  name: z.string().min(1).max(255),
  role: z.enum(['admin', 'member', 'viewer']).default('member'),
});

export type CreateBusinessUser = z.infer<typeof CreateBusinessUserSchema>;

// Backward compatibility aliases
export type BusinessId = string;
